import app from 'flarum/forum/app';

/**
 * JS mirror of Ppk2\Profile\Info\InfoRules (src/Info/InfoRules.php). Keep in sync.
 * The server always re-checks.
 */
export const KEY = 'ppk2-profile.lib.info.';
export const VISIBILITY = ['everyone', 'members', 'nobody'];
/** Flag next to a language (only flags that exist in assets/flags). */
export const LANGUAGE_FLAG = {
  ru: 'RU',
  en: 'GB',
  fi: 'FI',
  pl: 'PL',
  uk: 'UA',
  lv: 'LV',
  lt: 'LT',
  et: 'EE',
  sv: 'SE',
  no: 'NO',
  nb: 'NO',
  da: 'DK',
  cs: 'CZ',
  be: 'BY',
  fr: 'FR',
  is: 'IS'
};
export const LOCATION_MAX = 100;
export const BIO_MAX = 500;
export const AGE_MIN = 6;
export const AGE_MAX = 100;

/** «Страна»: fixed list (stored as these keys) + «…ввести вручную» (free text, max COUNTRY_MAX). */
export const COUNTRIES = ['LV', 'LT', 'EE', 'RU', 'UA', 'BY', 'PL', 'CZ', 'FI', 'SE', 'NO', 'DK', 'GB', 'IE', 'IS', 'FR', 'BE'];
export const COUNTRY_MAX = 50;
export const COUNTRY_PATTERN = /^[\p{L}\p{M}][\p{L}\p{M} .'’()\-]{1,49}$/u;
export const MANUAL = '__manual';

/** «Часовой пояс»: [key, IANA zone, standard UTC offset]. Helsinki = portal time and fallback. */
export const TIMEZONES = [['london', 'Europe/London', 0], ['warsaw', 'Europe/Warsaw', 1], ['helsinki', 'Europe/Helsinki', 2], ['moscow', 'Europe/Moscow', 3], ['samara', 'Europe/Samara', 4], ['yekaterinburg', 'Asia/Yekaterinburg', 5], ['omsk', 'Asia/Omsk', 6], ['novosibirsk', 'Asia/Novosibirsk', 7], ['irkutsk', 'Asia/Irkutsk', 8], ['yakutsk', 'Asia/Yakutsk', 9], ['vladivostok', 'Asia/Vladivostok', 10]];
export const PORTAL_TIMEZONE = 'Europe/Helsinki';

/** Time zone set at the moment a country is chosen (the player can change it afterwards). */
export const COUNTRY_TIMEZONE = {
  RU: 'Europe/Moscow',
  BY: 'Europe/Moscow',
  LV: 'Europe/Helsinki',
  LT: 'Europe/Helsinki',
  EE: 'Europe/Helsinki',
  FI: 'Europe/Helsinki',
  UA: 'Europe/Helsinki',
  PL: 'Europe/Warsaw',
  CZ: 'Europe/Warsaw',
  SE: 'Europe/Warsaw',
  NO: 'Europe/Warsaw',
  DK: 'Europe/Warsaw',
  FR: 'Europe/Warsaw',
  BE: 'Europe/Warsaw',
  GB: 'Europe/London',
  IE: 'Europe/London',
  IS: 'Europe/London'
};
export function locale() {
  return app.translator.getLocale && app.translator.getLocale() || app.data.locale || 'en';
}
export function flagUrl(code) {
  if (code && !COUNTRIES.includes(code) && code !== 'GB') return null;
  return code ? app.forum.attribute('assetsBaseUrl') + '/extensions/ppk2-profile/flags/' + code.toLowerCase() + '.png' : null;
}
function displayNames(type) {
  try {
    return new Intl.DisplayNames([locale()], {
      type
    });
  } catch (e) {
    return {
      of: c => c
    };
  }
}
const cap = s => s ? s.charAt(0).toLocaleUpperCase(locale()) + s.slice(1) : s;
export function countryName(key) {
  return app.translator.trans('ppk2-profile.lib.countries.' + key.toLowerCase());
}

/** [[key, localized name]] in the fixed order. */
export function countryOptions() {
  return COUNTRIES.map(c => [c, countryName(c)]);
}

/** Stored text -> list key (also a list name typed in the current language), or null = manual / empty. */
export function countryKey(text) {
  if (!text) return null;
  if (COUNTRIES.includes(text)) return text;
  const t = text.trim().toLocaleLowerCase();
  return COUNTRIES.find(c => String(countryName(c)).toLocaleLowerCase() === t) || null;
}

/** [key, params] or null for a typed country. */
export function checkCountry(text) {
  const v = (text || '').trim();
  if (!v || COUNTRIES.includes(v)) return null;
  if (v.length > COUNTRY_MAX) return ['ppk2-profile.lib.info.country_too_long', {
    max: COUNTRY_MAX
  }];
  return COUNTRY_PATTERN.test(v) ? null : ['ppk2-profile.lib.info.country_invalid', {}];
}

/** Installed language packs {code: native name}, e.g. {ru: 'Русский', en: 'English'} (Flarum's own list). */
export function languages() {
  return app.data.locales || {};
}
export function languageName(code, capital = true) {
  const native = languages()[code];
  if (native && capital) return native;
  const name = displayNames('language').of(code) || code;
  return capital ? cap(name) : name;
}
export function browserTimezone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || null;
  } catch (e) {
    return null;
  }
}

/** Current offset of a zone in minutes (e.g. 120), or null. */
export function offsetMinutes(tz, date = new Date()) {
  try {
    const part = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      timeZoneName: 'shortOffset'
    }).formatToParts(date).find(p => p.type === 'timeZoneName');
    if (!part) return null;
    const m = /GMT(?:([+-−])(\d{1,2})(?::(\d{2}))?)?/.exec(part.value);
    if (!m) return null;
    if (!m[1]) return 0;
    const sign = m[1] === '+' ? 1 : -1;
    return sign * (parseInt(m[2], 10) * 60 + (m[3] ? parseInt(m[3], 10) : 0));
  } catch (e) {
    return null;
  }
}

/**
 * The listed zone for any IANA zone: the same zone, else the same current offset,
 * else the nearest offset; unknown / empty -> Europe/Helsinki (portal time).
 */
export function pickTimezone(tz) {
  if (!tz) return PORTAL_TIMEZONE;
  const exact = TIMEZONES.find(z => z[1] === tz);
  if (exact) return exact[1];
  const off = offsetMinutes(tz);
  if (off === null) return PORTAL_TIMEZONE;
  let best = null;
  let bestDiff = Infinity;
  TIMEZONES.forEach(z => {
    const o = offsetMinutes(z[1]);
    const d = Math.abs((o === null ? z[2] * 60 : o) - off);
    if (d < bestDiff) {
      best = z[1];
      bestDiff = d;
    }
  });
  return best || PORTAL_TIMEZONE;
}

/** "UTC+2: Хельсинки, Киев, …" (+ «основное время портала» for Helsinki). */
export function timezoneLabel(z) {
  const [key,, off] = z;
  const head = 'UTC' + (off ? '+' + off : '+0') + ': ' + app.translator.trans('ppk2-profile.forum.info.timezone.zones.' + key);
  return z[1] === PORTAL_TIMEZONE ? head + ' — ' + app.translator.trans('ppk2-profile.forum.info.timezone.portal') : head;
}

/** Full years for "YYYY-MM-DD", or null. */
export function age(date, today = new Date()) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date || '');
  if (!m) return null;
  const y = +m[1],
    mo = +m[2],
    d = +m[3];
  const dt = new Date(Date.UTC(y, mo - 1, d));
  if (dt.getUTCFullYear() !== y || dt.getUTCMonth() !== mo - 1 || dt.getUTCDate() !== d) return null;
  let a = today.getFullYear() - y;
  if (today.getMonth() + 1 < mo || today.getMonth() + 1 === mo && today.getDate() < d) a--;
  return a < 0 ? null : a;
}

/** [key, params] or null */
export function checkBirthdate(date) {
  if (!date) return null;
  const a = age(date);
  if (a === null) return [KEY + 'birthdate_invalid', {}];
  if (a < AGE_MIN || a > AGE_MAX) return [KEY + 'birthdate_age', {
    min: AGE_MIN,
    max: AGE_MAX
  }];
  return null;
}
flarum.reg.add('ppk2-profile', 'forum/utils/infoRules', { KEY: KEY,VISIBILITY: VISIBILITY,LANGUAGE_FLAG: LANGUAGE_FLAG,LOCATION_MAX: LOCATION_MAX,BIO_MAX: BIO_MAX,AGE_MIN: AGE_MIN,AGE_MAX: AGE_MAX,COUNTRIES: COUNTRIES,COUNTRY_MAX: COUNTRY_MAX,COUNTRY_PATTERN: COUNTRY_PATTERN,MANUAL: MANUAL,TIMEZONES: TIMEZONES,PORTAL_TIMEZONE: PORTAL_TIMEZONE,COUNTRY_TIMEZONE: COUNTRY_TIMEZONE,locale: locale,flagUrl: flagUrl,countryName: countryName,countryOptions: countryOptions,countryKey: countryKey,checkCountry: checkCountry,languages: languages,languageName: languageName,browserTimezone: browserTimezone,offsetMinutes: offsetMinutes,pickTimezone: pickTimezone,timezoneLabel: timezoneLabel,age: age,checkBirthdate: checkBirthdate, });